/**
 * @fileoverview SEO utility functions for meta tags and hreflang.
 * @module shared/components/common/seoUtils
 */

const SITE_URL = 'https://abyssblock.com'

/** Supported languages for hreflang */
export const SUPPORTED_LANGUAGES = ['en', 'pl'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/** Default language for x-default hreflang */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en'

/**
 * Hreflang alternate link configuration
 */
export interface HreflangAlternate {
  /** Language code (e.g., 'en', 'pl') */
  lang: SupportedLanguage | 'x-default'
  /** Full URL for this language version */
  href: string
}

/**
 * Helper to generate hreflang alternates for a given path.
 * Use this for pages available in multiple languages at the same URL.
 *
 * @example
 * ```tsx
 * <PageMeta
 *   title="Home"
 *   description="..."
 *   hreflang={generateHreflang('/')}
 * />
 * ```
 */
export function generateHreflang(path: string): HreflangAlternate[] {
  const url = `${SITE_URL}${path}`
  return [
    { lang: 'en', href: url },
    { lang: 'pl', href: url },
    { lang: 'x-default', href: url },
  ]
}

/**
 * Generate hreflang alternates for localized landing pages.
 * Each language gets its own URL path (/en/, /pl/).
 *
 * @example
 * ```tsx
 * <PageMeta
 *   title="Home"
 *   description="..."
 *   hreflang={generateLandingHreflang('en')}
 *   canonicalPath="/en/"
 * />
 * ```
 */
export function generateLandingHreflang(_currentLang: SupportedLanguage): HreflangAlternate[] {
  return [
    { lang: 'en', href: `${SITE_URL}/en/` },
    { lang: 'pl', href: `${SITE_URL}/pl/` },
    { lang: 'x-default', href: `${SITE_URL}/` },
  ]
}

/**
 * Extract language from URL path.
 * Returns the language if path starts with /en or /pl, otherwise null.
 */
export function extractLangFromPath(pathname: string): SupportedLanguage | null {
  const match = pathname.match(/^\/(en|pl)(\/|$)/)
  return match ? (match[1] as SupportedLanguage) : null
}

/**
 * Detect preferred language from browser settings.
 */
export function detectBrowserLanguage(): SupportedLanguage {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE
  const browserLang = navigator.language.toLowerCase()
  if (browserLang.startsWith('pl')) return 'pl'
  return 'en'
}
